import * as yup from 'yup'
import * as set from 'lodash/set'
import * as moment from 'moment'
import { AnyDict } from 'interfaces'
import {
  BIRTH_DATE_TOO_OLD,
  BIRTH_DATE_TOO_YOUNG,
  CURRENCY_INVALID_SHORT,
  DATE_INVALID,
  TYPE_REQUIRED,
  ROUTING_NUMBER_ERROR,
  FEED_USERNAME_INVALID,
  NONZERO_CURRENCY_INVALID,
} from 'constants/errors'
import { validateDecimalCurrency, validateNotZeroCurrency } from 'helpers/formHelpers'
import { DATE_FORMAT_US } from 'constants/datetime'
import { NO_DIGITS_REGEX, ROUTING_NUMBER_REGEX } from 'constants/bounty'
import { USERNAME_VALIDATE_REGEX } from 'constants/feed'

// https://www.karltarvas.com/2020/01/10/yup-type-stringschema-is-not-assignable.html
export type YupShape<T> = {
  [K in keyof T]: T[K] extends string ? string : T[K];
}

export const NAME_REGEX_MESSSAGE = `Valid characters are A-Z a-z .-'`
export const SPECIAL_CHARS_REGEX = `^[a-zA-Z0-9._'-]`
export const SPECIAL_CHARS_REGEX_MESSSAGE = `Valid characters are A-Z a-z 0-9 ._-'`

const fullNameRegex = new RegExp(`${ SPECIAL_CHARS_REGEX }`)
const alphaNumericRegex = new RegExp(`${ SPECIAL_CHARS_REGEX }`)

export const baseDateSchema = yup.date()
  .transform((_currentValue, originalValue) => moment(originalValue, DATE_FORMAT_US).toDate())

export const birthDateSchema = baseDateSchema
  .typeError(DATE_INVALID)
  .max(moment().subtract(18, 'year').toDate(), BIRTH_DATE_TOO_YOUNG)
  .min(moment().subtract(120, 'year').toDate(), BIRTH_DATE_TOO_OLD)
  .required(TYPE_REQUIRED)

const VERIFICATION_CODE_MESSAGE = 'Code must be 4 digits'
export const verificationCodeSchema = yup.string()
  .nullable(true)
  .required(VERIFICATION_CODE_MESSAGE)
  .matches(/^[0-9]{4}$/, VERIFICATION_CODE_MESSAGE)

const ZIP_ERROR_MESSAGE = 'Must be 5 digits'
export const zipCodeSchema = yup.string()
  .nullable(true)
  .required(ZIP_ERROR_MESSAGE)
  .matches(/^[0-9]{5}$/, ZIP_ERROR_MESSAGE)

export const accountNumberSchema = yup.string()
  .min(5, 'Must be at least 5 digits')
  .max(17, 'Must be at most 17 digits')
  .required(TYPE_REQUIRED)

export const bankAccountTypeSchema = yup.string()
  .required(TYPE_REQUIRED)
  .matches(/^(?:savings|checking)$/)

export const routingNumberSchema = yup.string()
  .required(TYPE_REQUIRED)
  .test('routing number contains 9 digits', ROUTING_NUMBER_ERROR, (rawValue: string) => {
    const value = rawValue.replace(NO_DIGITS_REGEX, '')
    return ROUTING_NUMBER_REGEX.test(value)
  })

export const currencySchema = yup.mixed()
  .required(TYPE_REQUIRED)
  .test('decimal currency', CURRENCY_INVALID_SHORT, validateDecimalCurrency)

export const currencySchemaZeroProhibited = currencySchema
  .clone()
  .test('non zero', NONZERO_CURRENCY_INVALID, validateNotZeroCurrency)

export const usernameSchema = yup.string()
  .nullable(true)
  .required(TYPE_REQUIRED)
  .matches(USERNAME_VALIDATE_REGEX, FEED_USERNAME_INVALID)

// ^(?!00000)(?<zip>(?<zip5>\d{5})(?:[ -](?=\d))?(?<zip4>\d{4})?)$

export const fullNameSchema = yup.string()
  .required(TYPE_REQUIRED)
  .matches(fullNameRegex, NAME_REGEX_MESSSAGE)
  .max(50, 'Name must be less than 50 characters')
  .test('not-only-spaces', TYPE_REQUIRED, (name: string) => {
    return !!name && name.trim().length !== 0
  })

export const cardExpirySchema = yup.string()
  .required(TYPE_REQUIRED)
  .test('valid-expiry', 'Enter a valid date in the future', (value: string) => {
    if (/^(0[1-9]|1[0-2])\/[0-9]{2}$/i.test(value.trim())) {
      const today = new Date()
      const CurrentDate = moment(
        new Date(
          today.getFullYear() +
              '-' +
              (today.getMonth() + 1) +
              '-' +
              new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()
        )
      )
      const visaValue = value.split('/')
      const year = '20' + visaValue[1]
      const visaDate = new Date(parseInt(year, 10), parseInt(visaValue[0], 10), 0)
      return CurrentDate < moment(visaDate)
        ? true
        : false
    } else {
      return false
    }

  })

export const alphaNumericStringSchema = yup.string()
  .nullable(true)
  .matches(alphaNumericRegex, { message: SPECIAL_CHARS_REGEX_MESSSAGE, excludeEmptyString: true })
  .max(50, 'Must be less than 50 characters')

const parseErrors = (errors: yup.ValidationError) => {
  const rErrors: AnyDict = {}

  errors['inner'].forEach((error: yup.ValidationError) => {
    set(rErrors, error['path'], error['message'])
  })

  return rErrors
}

export const asyncValidator = (schema: any, options?: yup.ValidateOptions) => {
  return (values: AnyDict = {}): Promise<any> => {
    return new Promise((resolve, reject) => {
      schema.validate(values, { ...options, abortEarly: false })
        .then(() => resolve({}))
        .catch((error: any) => {
          const rErrors = parseErrors(error)

          reject(rErrors)
        })
    })
  }
}
